import { useState, useEffect, useCallback, useMemo } from 'react';
import { QueryField } from '../../types/QueryBuilder';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import { SIMPLE_QUERY_CATEGORY, getSimpleQueryFields } from '../../utils/queryBuilderUtils';
import useWorkspaceChange from '../useWorkspaceChange';

interface ApiSegmentField {
    label: string;
    name: string;
    placeholder: string;
    defaultOperator: string;
    valueEditorType: string;
    inputType: string | null;
    datatype: string;
    Categorie: string;
    values?: Array<{
        label: string;
        name: string | null;
    }>;
}

const convertApiFieldToQueryField = (apiField: ApiSegmentField): QueryField => {
    const datatype = apiField.datatype === 'DATE' ? 'DATE' : apiField.inputType === 'number' ? 'NUMBER' : 'TEXT';
    const inputType = apiField.inputType || 'text';
    
    let valueEditorType = apiField.valueEditorType;
    if (!valueEditorType) {
        if (inputType === 'date') valueEditorType = 'date';
        else if (apiField.values?.length) valueEditorType = 'select';
        else valueEditorType = 'text';
    }

    return {
        name: apiField.name,
        label: apiField.label || apiField.placeholder,
        Categorie: apiField.Categorie,
        datatype,
        defaultOperator: apiField.defaultOperator || '=',
        inputType,
        valueEditorType: valueEditorType as any,
        values: apiField.values?.map(v => ({ label: v.label, name: v.name || '' }))
    };
};

const FIELDS_CACHE_KEY = 'segment_fields';

export const useSegmentFields = () => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    // Scoped by workspace: switching workspace changes this key, so the fields (store list included)
    // of the previous workspace are never served as a stale cache hit for the new one.
    const cacheKey = `${FIELDS_CACHE_KEY}:${workspaceId ?? 'none'}`;

    const [fields, setFields] = useState<QueryField[]>(() =>
        cache?.get<QueryField[]>(cacheKey)?.data ?? []
    );
    const [loading, setLoading] = useState<boolean>(() =>
        !cache?.get<QueryField[]>(cacheKey)
    );
    const [error, setError] = useState<string | null>(null);

    const fetchFields = useCallback(async (forceRefresh = false) => {
        if (!isReady) return;
        if (!forceRefresh) {
            const hit = cache?.get<QueryField[]>(cacheKey);
            if (hit) { setFields(hit.data); setLoading(false); return; }
        }
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch('/getDataQuerySegmentList', { method: 'GET' });
            const apiFields: ApiSegmentField[] = Array.isArray(response) ? response : response.data || [];
            if (!apiFields.length) throw new Error('Aucun champ disponible');
            const converted = apiFields.filter(f => f.name).map(convertApiFieldToQueryField);
            setFields(converted);
            cache?.set(cacheKey, converted);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cache, cacheKey]);

    // Clears the previous workspace's fields (store list included) immediately on switch, instead
    // of leaving them visible until the refetch for the new workspace resolves.
    useWorkspaceChange(() => {
        setFields([]);
        setLoading(true);
    });

    useEffect(() => {
        fetchFields();
    }, [fetchFields]);

    const categories = useMemo(() =>
        [...new Set(fields.filter(f => f.Categorie && f.Categorie !== SIMPLE_QUERY_CATEGORY).map(f => f.Categorie))],
        [fields]
    );

    const simpleFields = useMemo(() => getSimpleQueryFields(fields), [fields]);

    const getFieldsByCategory = useCallback((category: string): QueryField[] =>
        fields.filter(f => f.Categorie === category),
        [fields]
    );

    return useMemo(() => ({
        fields,
        loading,
        error,
        categories,
        simpleFields,
        getFieldsByCategory,
        refetch: () => fetchFields(true)
    }), [fields, loading, error, categories, simpleFields, getFieldsByCategory, fetchFields]);
};
